import { roundToDecimals } from '@shared/helpers/math.helpers';

/**
 * Band along the gradient axis (CSS `px`). Values are **perceived** thickness on screen for
 * diagonal angles (see {@link getStripedBackground}).
 */
export type StripedGradientBand = {
  color: string;
  width: number;
};

/**
 * Builds a `repeating-linear-gradient` for striping. CSS angles: `0deg` up, clockwise.
 * {@link StripedBackgroundSpec.angle} is reduced modulo 360 for stable output strings.
 *
 * For non-axis angles, stop distances are scaled by **|sin θ| + |cos θ|** (e.g. √2 at ±45°)
 * so the repeat period along the gradient matches a whole-pixel **perpendicular** repeat —
 * same approach as scaling a 5px cycle to **5√2 ≈ 7.07px** so 2px/3px bands become **2√2 / 3√2**.
 * Values are kept as decimals; rounding stops to whole pixels breaks that alignment.
 */
export type StripedBackgroundSpec = {
  angle?: number;
  gap: StripedGradientBand;
  stripe: StripedGradientBand;
  /** `gap-first`: hatch over fill. `stripe-first`: stripe then gap (e.g. mezzanine). */
  order?: 'gap-first' | 'stripe-first';
};

/** At 45° this is √2; on horizontal/vertical gradients it is 1. */
const angleBandScale = (angleDeg: number): number => {
  const rad = (angleDeg * Math.PI) / 180;
  return Math.abs(Math.sin(rad)) + Math.abs(Math.cos(rad));
};

const isUnitScale = (f: number): boolean => Math.abs(f - 1) < 1e-6;

export const getStripedBackground = (
  options: StripedBackgroundSpec,
): string => {
  const { angle = -45, gap, stripe, order = 'gap-first' } = options;

  const effectiveAngle = angle % 360;
  const f = angleBandScale(effectiveAngle);
  const unit = isUnitScale(f);

  const gapPx = unit
    ? Math.max(1, Math.round(gap.width * f))
    : Math.max(0.01, roundToDecimals(gap.width * f, 2));
  const stripePx = unit
    ? Math.max(1, Math.round(stripe.width * f))
    : Math.max(0.01, roundToDecimals(stripe.width * f, 2));
  const periodPx = unit
    ? Math.max(2, gapPx + stripePx)
    : roundToDecimals(gapPx + stripePx, 2);

  const stops =
    order === 'gap-first'
      ? `${gap.color}, ${gap.color} ${gapPx}px, ${stripe.color} ${gapPx}px, ${stripe.color} ${periodPx}px`
      : `${stripe.color}, ${stripe.color} ${stripePx}px, ${gap.color} ${stripePx}px, ${gap.color} ${periodPx}px`;

  return `repeating-linear-gradient(${effectiveAngle}deg, ${stops})`;
};
